import { getCurrentCashDrawer } from '@/lib/services/cash-drawers/cash-drawers.api'
import { cashDrawerQueries } from '@/lib/services/cash-drawers/cash-drawers.query'
import {
  findPairedCashDrawerWorkstation,
  pairedCashDrawerMatchesLocation,
} from '@/lib/services/cash-drawers/cash-drawer-device'

import type { OrganizationId } from '@/lib/organization-scoping'
import type {
  CashDrawerIdInput,
  CashDrawerSession,
  CashDrawerWorkstation,
  CashDrawerStatus,
  CurrentCashDrawer,
  OpenCashDrawers,
} from './cash-drawers.types'
interface QueryState<T = unknown> {
  data?: T
  isError?: boolean
  isFetching?: boolean
  refetch?: () => Promise<{ data?: T; isError?: boolean } | undefined>
}
interface ManualTenderDrawerInput {
  allowSoleOpenSession?: boolean
  /** Whether the shop tracks tills must be read, never assumed off: a status unavailable or unread throws. */
  strictStatus?: boolean
  openDrawersQuery?: QueryState<OpenCashDrawers>
  canOperateCashDrawers: boolean
  currentDrawerQuery?: QueryState<CurrentCashDrawer>
  organizationId: OrganizationId
  pairedWorkstation?: CashDrawerWorkstation | null
  statusQuery?: QueryState<CashDrawerStatus>
  targetLocationId?: CashDrawerIdInput
  workstationId?: CashDrawerIdInput
  workstationsQuery?: QueryState<CashDrawerWorkstation[]>
}
export const CASH_DRAWER_DEVICE_SETUP_MESSAGE =
  'Set up this device from the Cash Drawer button before recording cash or check activity.'

export const CASH_DRAWER_STATUS_UNAVAILABLE_MESSAGE =
  "We couldn't verify this device's Cash Drawer. Check your connection and try again."

export function cashDrawerQueriesHaveError(
  statusQuery: QueryState<CashDrawerStatus>,
  ...trackingStates: QueryState[]
) {
  if (statusQuery?.isError || statusQuery?.data?.enabled !== true) return false

  return trackingStates.some((query) => query?.isError)
}

export function cashDrawerQueriesAreFetching(...queries: QueryState[]) {
  return queries.some((query) => query?.isFetching)
}

export function retryFailedCashDrawerQueries(...queries: QueryState[]) {
  return Promise.all(
    queries
      .filter((query) => query?.isError && query.refetch)
      .map(async (query) => query.refetch?.())
  )
}

export async function refreshCashDrawerSession(currentDrawerQuery: QueryState<CurrentCashDrawer>) {
  if (!currentDrawerQuery?.refetch) {
    return { isError: true, session: null }
  }

  try {
    const result = await currentDrawerQuery.refetch()

    return {
      isError: !result || result.isError === true,
      session: !result || result.isError ? null : (result.data?.session ?? null),
    }
  } catch {
    return { isError: true, session: null }
  }
}

export type ManualTenderDrawer =
  | { trackingEnabled: false; session: null; workstation: null }
  | {
      trackingEnabled: true
      session: CashDrawerSession
      workstation: CashDrawerSession['workstation'] | CashDrawerWorkstation
    }

export async function refreshManualTenderDrawer({
  allowSoleOpenSession = false,
  strictStatus = false,
  openDrawersQuery,
  canOperateCashDrawers,
  currentDrawerQuery,
  organizationId,
  pairedWorkstation,
  statusQuery,
  targetLocationId,
  workstationId,
  workstationsQuery,
}: ManualTenderDrawerInput): Promise<ManualTenderDrawer> {
  // Unknown/off/error status sends no attribution; API admission enforces current policy.
  // Tracked shops still verify the current session at each physical handoff. A cash refund (strictStatus)
  // reads the status now, whatever it last read: money handed back without its till is a refund the server
  // refuses, and a till turned on since the status was read is a till.
  let status = statusQuery?.data
  if (strictStatus) {
    // Read again, so a lookup that failed earlier recovers on the next try; blocked only while it still fails.
    status = await refetchCashDrawerQuery(statusQuery)
    if (status === undefined) {
      throw new Error(CASH_DRAWER_STATUS_UNAVAILABLE_MESSAGE)
    }
  } else if (status?.enabled !== true || statusQuery?.isError) {
    return { trackingEnabled: false, session: null, workstation: null }
  }
  if (status?.enabled !== true) {
    return { trackingEnabled: false, session: null, workstation: null }
  }
  if (!strictStatus) {
    try {
      status = await refetchCashDrawerQuery(statusQuery)
    } catch {
      return { trackingEnabled: false, session: null, workstation: null }
    }
    if (status?.enabled !== true) {
      return { trackingEnabled: false, session: null, workstation: null }
    }
  }

  if (!canOperateCashDrawers) {
    throw new Error(
      "You don't have permission to record cash or check activity while Cash Drawer tracking is on."
    )
  }

  if (!targetLocationId) {
    throw new Error('Choose an order location before recording cash or check activity.')
  }

  const workstations = ((await refetchCashDrawerQuery(workstationsQuery, [])) ?? []).filter(
    (workstation) => workstation.is_active && workstation.location?.is_active
  )
  const freshPairedWorkstation = findPairedCashDrawerWorkstation(
    workstations,
    workstationId,
    targetLocationId
  )

  if (!freshPairedWorkstation) {
    // Cancellation alone permits an unpaired device to use the sole open till at its order location.
    if (allowSoleOpenSession) {
      const openDrawers = await refetchCashDrawerQuery(openDrawersQuery, {
        data: [],
        enabled: true,
      })
      const sessions = (openDrawers?.data ?? []).filter(
        (session) => Number(session.location?.id) === Number(targetLocationId)
      )
      const [soleSession] = sessions
      if (soleSession && sessions.length === 1) {
        return { trackingEnabled: true, session: soleSession, workstation: soleSession.workstation }
      }
      throw new Error(
        sessions.length === 0
          ? 'Start a cash drawer for this order location before recording a cash or check refund.'
          : 'Set up this device with a register that has a cash drawer in use before recording a cash or check refund.'
      )
    }
    throw new Error(CASH_DRAWER_DEVICE_SETUP_MESSAGE)
  }

  if (!pairedCashDrawerMatchesLocation(freshPairedWorkstation, targetLocationId)) {
    throw new Error(
      `This device uses ${freshPairedWorkstation.name} at ${freshPairedWorkstation.location.name}, which does not match this order’s location.`
    )
  }

  let currentDrawer
  if (
    Number(pairedWorkstation?.id) === Number(freshPairedWorkstation.id) &&
    currentDrawerQuery?.refetch
  ) {
    currentDrawer = await refetchCashDrawerQuery(currentDrawerQuery)
  } else {
    currentDrawer = await getCurrentCashDrawer({
      queryKey: cashDrawerQueries.current({
        organizationId,
        workstationId: freshPairedWorkstation.id,
      }).queryKey,
    })
  }

  if (!currentDrawer?.session) {
    throw new Error(
      `Start the cash drawer for ${freshPairedWorkstation.name} before recording cash or check activity.`
    )
  }

  return {
    trackingEnabled: true,
    session: currentDrawer.session,
    workstation: freshPairedWorkstation,
  }
}

async function refetchCashDrawerQuery<T>(
  query?: QueryState<T>,
  fallback?: T
): Promise<T | undefined> {
  if (!query?.refetch) return query?.data ?? fallback

  const result = await query.refetch()
  if (!result) return query.data ?? fallback
  if (result.isError) throw new Error(CASH_DRAWER_STATUS_UNAVAILABLE_MESSAGE)

  return result.data ?? fallback
}
